/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Datepicker from 'react-tailwindcss-datepicker';
import dayjs from 'dayjs';
import { Button, Checkbox } from '@mui/material';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox, MultiComboBoxWithAll } from 'components/selects/ComboBox';
import { IconButton } from 'components/buttons/IconSVG';
import { DatePickerWrap } from 'components/inputs/DatePicker';
import { ecoYNcodes, MpItemCondition, MpItemTpCd } from 'models/mp/MpItem';
import { ElmEmpPopup } from 'pages/common/ElmEmpPopup';
import { EquipmentGroupPopUp } from 'pages/common/popup/EquipmentGroupPopUp';
import { EquipmentGroupCondition } from 'models/common/popup/EquipmentGroup';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import {
  BmkAtcTpCd,
  BmkTgtTpCd,
  BookmarkCondition,
  BookmarkDtail,
  BookmarkMaster,
} from 'models/common/Bookmark';
import { findBookmark, getCondBookmarks, saveCondBookmarks } from 'apis/common/Bookmark';
import { findMpClass } from 'apis/mp/MpEquipment';
import { EqpLvCd } from 'models/mp/MpEquipment';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { st } from 'components/inputs/CustomInput';
import useSessionStore from 'stores/useSessionStore';
import MpMyBookmarkModal from '../MpMyBookmarkModal';
import { useCommonModal } from '../../../hooks/useCommonModal';
import { useMessageBar } from '../../../hooks/useMessageBar';
import _ from 'lodash';
import { getFactoryList } from 'apis/mp/MpFactory';
import { MpPlntList } from 'models/mp/MpPlnt';

/**
 * @description 설비변경점 등록 및 조회 - 조회영역
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  25.10.14    jihwan89       설비변경점 개선사항 v5.2 68행 - 담당자 선택버튼을 엔터 시 팝업 오픈
 *  25.10.15    jihwan89       설비변경점 개선사항 v5.2 3행 - 조회조건 Multi 로 변경 항목 : 법인, MP분류, 공정
 *  25.10.27    jihwan89       설비변경점 개선사항 v5.2 30행 - 조회조건 북마크 목록 추가
 *  26.02.04    jihwan89       수평전개 진척현황 조회화면 기능개선
 *                             1) 법인, 공정 멀티콤보박스 전체값 추가
 *                             2) 조회조건 수평전개 Item등록 포함, 대표호기 Item 포함 조건 추가
 *  26.03.05    jihwan89       수평전개 진척현황 조회조건 factory 추가
 *  26.03.18    jihwan89       법인, factory(수평전개진척현황) 조회 콤보박스 높이 조정
 *  26.03.23    jihwan89       E20260310-000615 법인, 공정 조회 조건 선택에 따른 Factory 매핑 목록 노출
 *                             1) 법인, 공정에 맞는 팩토리 매핑 목록 노출
 *                             2) 설비군이 NND일때 ELECTROD 고정 및 팩토리 Assy 목록만 노출
 *                             3) NND 일떄 ELECTROD 외 선택 시 설비군 초기화 및 팩토리목록 변경 노출
 */

interface DisplayCondition {
  reqMgrNm?: string; // 기술제안자명
  dataInsUserNm?: string; // 등록자명
}

type PageType = 'item' | 'tracking' | 'mybook' | 'summary';

interface Props {
  type?: PageType;
  isDefaultSearch?: boolean;
  onHandleSearch: (condition) => void;
  changeBookmark?: (bmkGrId: string) => void;
}

interface PageLocationState {
  copCd?: string;
  itemStatCd?: string;
  mpItemTpCd?: string;
  spshRevCtn?: string;
}

const MPCommonSearch = ({ isDefaultSearch = false, ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const [code, setCode] = useState<any>();
  const [errors, setErrors] = useState<any>({});
  const [userData, setUserData] = useState<any>('');
  const { state } = useLocation();
  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);
  const [isOpenMpMyBookmarkModal, setOpenMpMyBookmarkModal] = useState<boolean>(false);
  const [isOpenEquipmentGroupPopUp, setOpenEequipmentGroupPopUp] = useState<boolean>(false);
  const [isOpenElmEmpPopup, setOpenElmEmpPopup] = useState<boolean>(false);
  const [displayCondition, setDisplayCondition] = useState<DisplayCondition>({});
  const [isOpenSearchBox, setOpenSearchBox] = useState<boolean>(false);
  const [empPopupCondition, setEmpPopupCondition] = useState({
    fieldId: '',
    initParam: '',
  });
  const [changeCondition, setChangeCondition] = useState<boolean>(false);
  const defaultCondition = {
    mpClsfCd: '',
    mpClsfCds: [],
    copCd: '',
    copCds: [],
    procCd: '',
    procCds: [],
    startDataInsDtm: dayjs().add(-1, 'year').format('YYYY.MM.DD'),
    endDataInsDtm: dayjs().format('YYYY.MM.DD'),
  };

  const defaultTrackingCondition = {
    mpClsfCd: '',
    mpClsfCds: [],
    copCd: '',
    copCds: [],
    procCd: '',
    procCds: [],
    startDataInsDtm: dayjs().add(-1, 'year').format('YYYY.MM.DD'),
    endDataInsDtm: dayjs().format('YYYY.MM.DD'),
    heMp0001StatYn: 'Y', // 조회 시 수평전개, 설비변경점 수평전개의 Item등록 상태값(MP001)을 포함/미포함
    mpStatYn: 'N', // 조회 시 Item의 설비변경점 상태값(MP)을 포함/미포함
    prdnPlntCd: '', // 26/02.23 팩토리 조회조건 추가
    prdnPlntCds: [], // 26/02.23 팩토리 조회조건 추가
  };

  const [mpItemCondition, setMpItemCondition] = useState<MpItemCondition>(
    props.type === 'summary' ? defaultTrackingCondition : defaultCondition
  );

  const [bookmarkCodition, setBookmarkCondition] = useState<MpItemCondition>(defaultCondition);
  // MP분류 필수값 여부
  const isRequiredMpClsfCd = useMemo(() => 'tracking' === props.type, [props.type]);
  const isTrackingPage = useMemo(() => 'tracking' === props.type, [props.type]);
  const isMyBookPage = useMemo(() => 'mybook' === props.type, [props.type]);

  const heYNcodes: Code[] = [
    { cmnCd: 'Y', cmnCdNm: String(t('mp.label.진행', '진행')) },
    { cmnCd: 'N', cmnCdNm: String(t('mp.label.보류', '보류')) },
  ];

  const testSettingCondiiton: Code[] = [
    { cmnCd: 'INS_COND1', cmnCdNm: String(t('mp.label.조건설정1', '조건설정1')) },
    { cmnCd: 'INS_COND2', cmnCdNm: String(t('mp.label.조건설정2', '조건설정2')) },
    { cmnCd: 'INS_COND3', cmnCdNm: String(t('mp.label.조건설정3', '조건설정3')) },
    { cmnCd: 'INS_COND4', cmnCdNm: String(t('mp.label.조건설정4', '조건설정4')) },
    { cmnCd: 'INS_COND5', cmnCdNm: String(t('mp.label.조건설정5', '조건설정5')) },
  ];

  const [bmkCmnCdCondition, setBmkCmnCdCondition] = useState<string>(); // 조건설정 value
  const [changedBmkCmnCdCond, setChangedBmkCmnCdCond] = useState<boolean>(false); // 조건설정 변경
  const [factoryCode, setFactoryCode] = useState<any>([]);

  useEffect(() => {
    const init = async () => {
      await getCommonCodesForGrid();
    };
    init();

    // 개인설정(톱니바퀴) 우선 적용
    if (
      !_.isEmpty(useSessionStore.getState().mpClsfType) ||
      !_.isEmpty(useSessionStore.getState().elmCopCd) ||
      !_.isEmpty(useSessionStore.getState().prdnProcCd)
    ) {
      setTimeout(() => {
        const userSettingCondition = {
          mpClsfCd: useSessionStore.getState().mpClsfType || '',
          mpClsfCds: _.isEmpty(useSessionStore.getState().mpClsfType)
            ? []
            : [useSessionStore.getState().mpClsfType],
          copCd: useSessionStore.getState().elmCopCd || '',
          copCds: _.isEmpty(useSessionStore.getState().elmCopCd)
            ? []
            : [useSessionStore.getState().elmCopCd],
          procCd: useSessionStore.getState().prdnProcCd || '',
          procCds: _.isEmpty(useSessionStore.getState().prdnProcCd)
            ? []
            : [useSessionStore.getState().prdnProcCd],
        };

        setMpItemCondition((prev) => ({ ...prev, ...userSettingCondition }));

        if (props.type === 'summary') {
          const summaryCondition = {
            ...userSettingCondition,
            heMp0001StatYn: 'Y',
            mpStatYn: 'N',
          };
          handleSearch(summaryCondition);
        } else {
          handleSearch(userSettingCondition);
        }
      }, 100);
    } else {
      handleSearch(mpItemCondition);
    }
  }, []);

  useEffect(() => {
    if (state) {
      setLocationState(state);
    }
  }, [state]);

  useEffect(() => {
    if (locationState) {
      setTimeout(() => {
        setMpItemCondition((prev) => ({
          ...prev,
          mpItemTpCd: locationState?.mpItemTpCd || '',
          itemStatCd: locationState?.itemStatCd || '',
          spshRevCtn: locationState?.spshRevCtn || '',
        }));
      }, 100);
    }
  }, [locationState]);

  useEffect(() => {
    if (isDefaultSearch) {
      handleSearch(mpItemCondition);
    }
  }, [isDefaultSearch]);

  useEffect(() => {
    if (changeCondition) {
      handleSearch(mpItemCondition);
    }
  }, [mpItemCondition.heMp0001StatYn, mpItemCondition.mpStatYn]);

  /*
  // [25.08.25] 공정 > MP분류에 따른 옵션 조건 사용안함 (ex. CMN, UT 등)
  useEffect(() => {
    changeSearchConditionByMpClsfCd();
  }, [mpItemCondition?.mpClsfCd, code?.mpClsfCd]);
  */

  useEffect(() => {
    changeSearchConditionByIprvClsfLv1Cd();
  }, [mpItemCondition?.iprvClsfLv1Cd]);

  useEffect(() => {
    if (userData != null && userData != '') {
      const fieldId = userData.fieldId;
      const userInfo = userData.userInfo[0] || {};
      if (fieldId) {
        setMpItemCondition({
          ...mpItemCondition,
          [fieldId.replace('Nm', 'Id')]: userInfo.userId,
        });
        setDisplayCondition({
          ...displayCondition,
          [fieldId]: userInfo.empNm,
        });
      }
    }
  }, [userData]);

  const getCommonCodesForGrid = async () => {
    const mpClsfCd: Code[] = await getCommonCodeNames('MP_CLSF_CD'); // MP분류
    const mpItemTpCd: Code[] = await getCommonCodeNames('MP_ITEM_TP_CD');
    const mpItemStatus: Code[] = await getCommonCodeNames('MP_ITEM_STAT_CD');
    const chgProgStatCd: Code[] = await getCommonCodeNames('CHG_PROG_STAT_CD');
    const elmCopCd: Code[] = await getCommonCodeNames('ELM_COP_CD'); // 법인코드
    const mpImprTypeLvl1: Code[] = await getCommonCodeNamesCondition({
      cmnGrCd: 'MP_IMPR_TYPE_LVL1',
      // optValCtn1: optValCtn1, // [25.07.30] 개선목적 Level1 옵션1값 조건 사용안함 (ex. CMN, UT 등)
    }); // 개선목적 Level1 (iprvClsfLv1Cd)
    const prdnProcCd: Code[] = await getCommonCodeNamesCondition({
      cmnGrCd: 'PRDN_PROC_CD',
      optValCtn2: 'Y',
      // optValCtn6: optValCtn1, // [25.08.25] 공정 > MP분류에 따른 옵션 조건 사용안함 (ex. CMN, UT 등)
    });

    const bookmark: BookmarkMaster[] = await findBookmark(BmkAtcTpCd.MP_LIST);
    const bookmarkCodes: Code[] = (bookmark || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.bmkGrId,
          cmnCdNm: cur.bmkGrDesc,
        } as Code,
      ],
      [] as Code[]
    );

    const initSearchParam = {
      copCd: '',
      copCds: [],
    };
    getFactoryList(initSearchParam).then((result: MpPlntList[]) => {
      if (result !== null) {
        setFactoryCode(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });

    // 사용자 설정에 의한 MP분류 초기값이 분류코드에 없는 값인 경우 빈 값으로 변경
    // const chk = (mpClsfCd || []).filter((o) => o.cmnCd === mpItemCondition?.mpClsfCd);
    // if (chk.length < 1) {
    //   setMpItemCondition((prev) => ({
    //     ...prev,
    //     mpClsfCds: '',
    //   }));
    // }

    setCode({
      ...code,
      mpClsfCd: mpClsfCd,
      mpItemTpCd: mpItemTpCd,
      mpItemStatus: mpItemStatus,
      bookmarkCodes: bookmarkCodes,
      chgProgStatCd: chgProgStatCd,
      mpImprTypeLvl1: mpImprTypeLvl1,
      elmCopCd: elmCopCd,
      prdnProcCd: prdnProcCd,
    });
  };

  /**
   * MP분류 변경에 따른 검색조건 값 설정 변경
   * [25.08.25] 공정 > MP분류에 따른 옵션 조건 사용안함 (ex. CMN, UT 등)
   * 재설정 항목
   * 1. 공정
  const changeSearchConditionByMpClsfCd = async () => {
    setMpItemCondition((prev) => ({
      ...prev,
      procCd: '', // 공정
    }));

    setCode({
      ...code,
      prdnProcCd: [],
    });

    // MP 분류에 따른 검색조건 코드 조회
    if (mpItemCondition?.mpClsfCd) {
      const c = (code?.mpClsfCd || []).filter((o) => o.cmnCd === mpItemCondition?.mpClsfCd);
      if (c.length) {
        const optValCtn1 = c[0].optValCtn1;
        // 1. 공정
        const prdnProcCd: Code[] = await getCommonCodeNamesCondition({
          cmnGrCd: 'PRDN_PROC_CD',
          optValCtn2: 'Y',
          optValCtn6: optValCtn1,
        });

        setCode({
          ...code,
          prdnProcCd: prdnProcCd,
        });
      }
    }
  };
  */

  /**
   * 개선목적 Level1 변경에 따른 검색조건 값 설정 변경
   *
   * 재설정 항목
   * 1. 개선목적 Level2
   */
  const changeSearchConditionByIprvClsfLv1Cd = async () => {
    setMpItemCondition({
      ...mpItemCondition,
      iprvClsfLv2Cd: '', // 개선목적 Level2
      iprvClsfLv3Cd: '', // 개선목적 Level3
    });

    setCode({
      ...code,
      mpImprTypeLvl2: [],
      mpImprTypeLvl3: [],
    });

    // 개선목적 Level1에 따른 검색조건 코드 조회
    if (mpItemCondition?.iprvClsfLv1Cd) {
      // 1. 개선목적 Level2 (iprvClsfLv2Cd)
      const mpImprTypeLvl2: Code[] = await getCommonCodeNamesCondition({
        cmnGrCd: 'MP_IMPR_TYPE_LVL2',
        optValCtn1: mpItemCondition?.iprvClsfLv1Cd,
      });
      setCode({
        ...code,
        mpImprTypeLvl2: mpImprTypeLvl2,
      });
    }
  };

  /**
   * 개선목적 Level2 변경에 따른 검색조건 값 설정 변경
   *
   * 재설정 항목
   * 1. 개선목적 Level3
   */
  const changeSearchConditionByIprvClsfLv2Cd = async (iprvClsfLv2Cd?: string) => {
    setMpItemCondition({
      ...mpItemCondition,
      iprvClsfLv2Cd: iprvClsfLv2Cd,
      iprvClsfLv3Cd: '', // 개선목적 Level3
    });

    let mpImprTypeLvl3 = [] as Code[];
    // 개선목적 Level2에 따른 검색조건 코드 조회
    if (iprvClsfLv2Cd) {
      // 1. 개선목적 Level3 (iprvClsfLv3Cd)
      mpImprTypeLvl3 = await getCommonCodeNamesCondition({
        cmnGrCd: 'MP_IMPR_TYPE_LVL3',
        optValCtn2: iprvClsfLv2Cd,
      });
    }
    setCode((prev) => ({
      ...prev,
      mpImprTypeLvl3: mpImprTypeLvl3,
    }));
  };

  const handleDisplayConditionChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const name = e.target.name;
    setDisplayCondition({
      ...displayCondition,
      [name]: e.target.value,
    });

    setMpItemCondition({
      ...mpItemCondition,
      [name.replace('Nm', 'Id')]: '',
    });
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setMpItemCondition({
      ...mpItemCondition,
      [e.target.name]: e.target.value,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(mpItemCondition);
    }
  };

  const handleMgrKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setEmpPopupCondition({
        fieldId: 'reqMgrNm',
        initParam: displayCondition.reqMgrNm || '',
      });
      setOpenElmEmpPopup(true);
    }
  };

  // 26.03.23 수평전개 진척현황 조회조건 법인 및 공정 팩토리 매핑
  useEffect(() => {
    mpItemCondition.prdnPlntCds = [];
    mpItemCondition.prdnPlntCd = '';

    const spiltCopCd = mpItemCondition.copCd?.split(','); // 법인 콤보박스 선택

    let filter: Code[] = [];

    // 법인이 선택되지 않은 경우는 팩토리목록 전체
    if (_.isEmpty(mpItemCondition.copCd)) {
      filter = factoryCode.map((o) => ({
        cmnCd: o.prdnPlntCd,
        cmnCdNm: o.prdnPlntNm,
      }));
      setCode({ ...code, prdnPlntCd: filter });
    }
    // 법인이 선택된 경우에는 법인과 매핑된 팩토리 필터
    if (!_.isEmpty(mpItemCondition.copCd)) {
      filter = factoryCode
        ?.filter((o) => spiltCopCd?.includes(o.copCd))
        .map((o) => ({ cmnCd: o.prdnPlntCd, cmnCdNm: o.prdnPlntNm }));
      setCode({ ...code, prdnPlntCd: filter });
    }
    // 공정이 선택된 경우
    if (!_.isEmpty(mpItemCondition.procCd)) {
      // 공정과 팩토리 매핑 키워드
      const keyword = {
        A: 'Assy',
        E: 'Elec',
        F: 'Form',
        P: 'Pack',
      };
      const spiltProcCd = mpItemCondition.procCd?.split(',');

      const key = spiltProcCd?.map((o) => keyword[o]);
      let filteredFactoryList = filter?.filter((item) => {
        return key?.some((key) => item?.cmnCdNm?.includes(key));
      });

      if (key?.some((key) => key.includes('E'))) {
        // 설비군이 NND 일때 Assy 팩토리만 노출
        if (mpItemCondition.eqclId === 'NND') {
          filteredFactoryList = filter?.filter((item) => item.cmnCdNm?.includes('Assy'));
        } else {
          // EP2_OC_자동차 전극은 따로 추가.
          const ep2_oc_elec: Code[] = filter.filter((item) => {
            return item.cmnCd === 'F041';
          });

          filteredFactoryList = [...filteredFactoryList, ...ep2_oc_elec].sort((a, b) => {
            const cmnCdA = a.cmnCd || '';
            const cmnCdB = b.cmnCd || '';
            return cmnCdA.localeCompare(cmnCdB);
          });
        }
      }
      setCode({ ...code, prdnPlntCd: filteredFactoryList });
    }
  }, [mpItemCondition.copCd, factoryCode, mpItemCondition.procCd]);

  /**
   * 설비분류체계 변경에 따른 검색조건 값 설정 변경
   *
   * 재설정 항목
   * 1. 설비 분류 Level1 (Main)
   * 2. 설비 분류 Level2 (Machine)
   * 3. 설비 분류 Level3 (Unit)
   */
  const changeSearchConditionByEqpClsfNo = (eqpLvCd: EqpLvCd, eqpClsfNo?: string) => {
    if (eqpClsfNo) {
      const conditionKey = EqpLvCd.MAIN === eqpLvCd ? 'eqpClsfNo' : 'upprEqpClsfStrcNo';
      const condition = {
        [conditionKey]: eqpClsfNo,
        eqpLvCd: eqpLvCd,
      };
      findMpClass(condition).then((result) => {
        if ((result || []).length) {
          const eqpClsfNoCodes: Code[] = (result || []).reduce(
            (acc, cur) => [
              ...acc,
              {
                cmnCd: cur.eqpClsfStrcNo,
                cmnCdNm: cur.eqpClsfStrcNm,
              } as Code,
            ],
            [] as Code[]
          );

          const codeKey =
            EqpLvCd.MAIN === eqpLvCd
              ? 'eqpLv1Codes'
              : EqpLvCd.MACHINE === eqpLvCd
              ? 'eqpLv2Codes'
              : 'eqpLv3Codes';
          setCode({
            ...code,
            [codeKey]: eqpClsfNoCodes,
          });
        }
      });
    }
  };

  const handleSearch = (condition: MpItemCondition) => {
    if (isRequiredMpClsfCd && !condition.mpClsfCd) {
      setErrors((prev) => ({
        ...prev,
        mpClsfCd: true,
      }));
      return;
    }
    props?.onHandleSearch &&
      props.onHandleSearch({
        ...condition,
        // 진척현황인 경우 수평전개 고정
        mpItemTpCd: isTrackingPage ? MpItemTpCd.HE : condition?.mpItemTpCd || '',
      });
  };

  const handleOnChange = (name: string, value: string) => {
    setMpItemCondition({
      ...mpItemCondition,
      [name]: value,
    });
    setErrors({ ...errors, [name]: false });
  };

  useEffect(() => {
    if (changedBmkCmnCdCond) {
      getCondBookmark();
    }
  }, [bmkCmnCdCondition]);

  const getCondBookmark = async () => {
    const condition = {
      bmkAtcTpCd: BmkAtcTpCd.MP_ITEM,
      bmkTgtTpCd: BmkTgtTpCd.INQ_COND,
      optValCtn1: bmkCmnCdCondition, // 조회조건설정 명
      bmkGrDesc: location.pathname,
    } as BookmarkCondition;

    await getCondBookmarks(condition).then((res) => {
      if (!_.isEmpty(res?.data)) {
        const mapped: any = Object.values(res.data || []).reduce((acc, cur) => {
          const key = cur.bmkTgtId;
          const value = cur.optValCtn1;
          acc[key || ''] = value;
          return acc;
        }, {});

        const mpClsfCd = mapped?.mpClsfCd || '';
        const arrMpClsfCd = _.isEmpty(mpClsfCd) ? [] : mpClsfCd?.split(',');

        const copCd = mapped?.copCd || '';
        const arrCopCd = _.isEmpty(copCd) ? [] : copCd?.split(',');

        const procCd = mapped?.procCd || '';
        const arrProcCd = _.isEmpty(procCd) ? [] : procCd?.split(',');

        const condition = {
          ...mapped,
          mpClsfCds: arrMpClsfCd,
          copCds: arrCopCd,
          procCds: arrProcCd,
        };

        setDisplayCondition({ reqMgrNm: condition?.reqMgrNm });
        setMpItemCondition({ ...condition });
        handleSearch(condition);
        setChangedBmkCmnCdCond(false);
      }
    });
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox style={{ minWidth: '0px' }}>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>
                  <span className={isRequiredMpClsfCd ? 'dot' : ''}>
                    {String(t('mp.label.분류', '분류'))}
                  </span>
                </label>
                <MultiComboBox
                  id="mpClsfCd"
                  options={code?.mpClsfCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={mpItemCondition.mpClsfCds || []}
                  onChange={(value) => {
                    if (isRequiredMpClsfCd) {
                      setErrors((prev) => ({
                        ...prev,
                        mpClsfCd: false,
                      }));
                    }
                    handleOnChange('mpClsfCd', value.join());
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('mp.label.법인', '법인')}</label>
                <MultiComboBoxWithAll
                  id="copCd"
                  options={code?.elmCopCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={mpItemCondition.copCds || []}
                  onChange={(value) => {
                    handleOnChange('copCd', value.join());
                  }}
                  height={'650px'} // 목록 높이 추가
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('mp.label.공정', '공정'))}</label>
                <MultiComboBoxWithAll
                  id="procCd"
                  options={code?.prdnProcCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={mpItemCondition.procCds || []}
                  onChange={(value) => {
                    // 설비군이 NND 인데 공정이 ELECTRODE 가 아닐때 메세지 노출 및 설비군 '' 적용
                    if (mpItemCondition.eqclId === 'NND' && !_.isEqual(value, ['E'])) {
                      openMessageBar({
                        type: 'warning',
                        content: t(
                          'com.label.NND의 경우 ELECTRODE 선택이 필수입니다.',
                          'NND의 경우 ELECTRODE 선택이 필수입니다.'
                        ),
                      });
                      setMpItemCondition({ ...mpItemCondition, eqclId: '', procCd: value.join() });
                      return;
                    } else {
                      handleOnChange('procCd', value.join());
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('com.label.설비군', '설비군'))}</label>
                <CustomInputWithSearch
                  name="eqclId"
                  className="find"
                  placeholder={String(
                    t('mp.msg.설비군을 선택해 주세요.', '설비군을 선택해 주세요.')
                  )}
                  value={mpItemCondition.eqclId}
                  onChange={handleConditionChange}
                  onSearchClick={() => setOpenEequipmentGroupPopUp(true)}
                  isError={errors?.eqclId}
                  msgError={String(t('mp.msg.설비군을 선택해 주세요.', '설비군을 선택해 주세요.'))}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('mp.label.Item 명', 'Item 명'))}</label>
                <CustomInputWithSearch
                  name="itemNm"
                  placeholder={String(
                    t('mp.msg.Item명을 입력해 주세요.', 'Item명을 입력해 주세요.')
                  )}
                  value={mpItemCondition?.itemNm}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('mp.label.진행단계', '진행단계'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.button.선택', '선택'))}
                  options={code?.mpItemTpCd}
                  defaultValue={mpItemCondition?.mpItemTpCd}
                  onChange={(value) =>
                    setMpItemCondition({ ...mpItemCondition, mpItemTpCd: value as string })
                  }
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('mp.label.상태', '상태'))}</label>
                <ComboBox
                  placeholder={String(t('com.button.선택', '선택'))}
                  options={code?.mpItemStatus}
                  defaultValue={mpItemCondition?.itemStatCd}
                  onChange={(value) =>
                    setMpItemCondition({
                      ...mpItemCondition,
                      itemStatCd: value as string,
                    })
                  }
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('mp.label.조회조건설정', '조회조건설정'))}</label>
                {/* 검색조건즐겨찾기 */}
                <ComboBox
                  placeholder={String(t('com.button.선택', '선택'))}
                  options={testSettingCondiiton}
                  defaultValue={''}
                  onChange={(value) => {
                    setBmkCmnCdCondition(value);
                    setChangedBmkCmnCdCond(true);
                  }}
                />
              </SearchCols>
            </SearchRows>
            {/* heMp0001StatYn: 'Y', // 수평전개, 설비변경점 수평전개의 Item등록 상태값을 조회(default)
                mpStatYn: 'N', // 설비변경점 상태의 Item을 조회하지 않음(default) */}
            {props.type === 'summary' && (
              <SearchRows className="FourCol">
                <SearchCols>
                  <label>{t('mp.label.Factory', 'Factory')}</label>
                  <MultiComboBoxWithAll
                    id="prdnPlntCd"
                    options={code?.prdnPlntCd || []}
                    placeholder={String(t('com.label.전체', '전체'))}
                    defaultValue={mpItemCondition.prdnPlntCds || []}
                    onChange={(value) => {
                      handleOnChange('prdnPlntCd', value.join());
                    }}
                    height={'650px'} // 목록 높이 추가
                  />
                </SearchCols>
                <SearchCols className="flex">
                  <label htmlFor="heMp0001StatYn" style={{ paddingTop: '0px', fontSize: '10px' }}>
                    {t('mp.label.수평전개 ITEM등록 상태 포함', '수평전개 ITEM등록 상태 포함')}
                  </label>
                  <Checkbox
                    id="heMp0001StatYn"
                    name="heMp0001StatYn"
                    css={st.checkbox}
                    className="custom"
                    checked={mpItemCondition?.heMp0001StatYn === 'Y'}
                    onClick={(e) => {
                      setMpItemCondition({
                        ...mpItemCondition,
                        heMp0001StatYn: mpItemCondition.heMp0001StatYn === 'Y' ? 'N' : 'Y',
                      });
                      setChangeCondition(true);
                    }}
                    disableRipple
                  />
                </SearchCols>
                <SearchCols className="flex">
                  <label htmlFor="mpStatYn" style={{ paddingTop: '0px', fontSize: '10px' }}>
                    {t('mp.label.대표호기 검증 중 ITEM 포함', '대표호기 검증 중 ITEM 포함')}
                  </label>
                  <Checkbox
                    id="mpStatYn"
                    name="mpStatYn"
                    css={st.checkbox}
                    className="custom"
                    checked={mpItemCondition?.mpStatYn === 'Y'}
                    onClick={(e) => {
                      setMpItemCondition({
                        ...mpItemCondition,
                        mpStatYn: mpItemCondition.mpStatYn === 'Y' ? 'N' : 'Y',
                      });
                      setChangeCondition(true);
                    }}
                    disableRipple
                  />
                </SearchCols>
              </SearchRows>
            )}
          </InputBox>
          <SearchBoxButtons
            onBookmark={() => {
              openCommonModal({
                modalType: 'confirm',
                content: t(
                  'com.label.설정된 조회 조건을 즐겨찾기로 지정하시겠습니까?',
                  '설정된 조회 조건을 즐겨찾기로 지정하시겠습니까?'
                ),
                yesCallback: async () => {
                  if (_.isEmpty(bmkCmnCdCondition)) {
                    openMessageBar({
                      type: 'warning',
                      content: t(
                        'com.label.조건설정을 선택해 주세요.',
                        '조건설정을 선택해 주세요.'
                      ),
                    });
                    return;
                  }
                  const addMgrNmCondition = {
                    ...mpItemCondition,
                    reqMgrNm: displayCondition?.reqMgrNm || '',
                  };

                  const detailList = Object.entries(addMgrNmCondition).map(([key, value]) => ({
                    bmkTgtId: key,
                    optValCtn1: value,
                  }));

                  //필요없는 검색 조건은 제외
                  const excludedCondList = detailList.filter((item) => {
                    const id = item.bmkTgtId.toLowerCase();
                    return !id.endsWith('dtm') && !id.endsWith('cds');
                  });

                  const notEmptyCondeList = excludedCondList.filter((item) => {
                    return !_.isEmpty(item.optValCtn1);
                  });

                  const master = {
                    bmkAtcTpCd: BmkAtcTpCd.MP_ITEM,
                    bmkTgtTpCd: BmkTgtTpCd.INQ_COND,
                    bmkGrDesc: location.pathname,
                    optValCtn1: bmkCmnCdCondition, //조건설정 value
                    detailList: notEmptyCondeList,
                  } as BookmarkMaster;

                  saveCondBookmarks(master).then((res) => {
                    openMessageBar({
                      type: res.successOrNot === 'Y' ? 'confirm' : 'error',
                      content:
                        res.successOrNot === 'Y'
                          ? t('com.label.저장되었습니다.', '저장되었습니다.')
                          : t(
                              'com.label.저장 중 오류가 발생했습니다.',
                              '저장 중 오류가 발생했습니다.'
                            ),
                    });
                  });
                },
              });
            }}
            onReset={() => {
              /**
               * 1. 초기화 버튼은 모든 조회 조건을 초기화 한다.
               *    다시 나갔다 오면 조회 조건 설정된 걸로 보여준다.
               * 2. 공정, 법인 등 개인 설정값과 조회 조건 저장된 것은 조회 조건 저장된 것을 우선으로 한다.
               *
               * - 검색조건 중 등록일은 즐겨찾기에서 제외한다.
               */
              setTimeout(() => {
                // setMpItemCondition((prev) => defaultCondition);
                setMpItemCondition((prev) =>
                  Object.keys(prev).reduce((acc, cur) => Object.assign(acc, { [cur]: '' }), {})
                );
                setMpItemCondition((prev) => ({
                  ...prev,
                  mpClsfCds: [],
                  copCds: [],
                  procCds: [],
                  prdnPlntCds: [],
                  heMp0001StatYn: 'Y',
                  mpStatYn: 'N',
                }));
                setTimeout(() => {
                  const dateInput = document.querySelector('.datepickerInput') as HTMLInputElement;
                  if (dateInput) dateInput.value = '';
                }, 100);
              }, 100);
              setDisplayCondition(
                (prev) =>
                  Object.keys(prev).reduce(
                    (acc, cur) => Object.assign(acc, { [cur]: '' }),
                    {}
                  ) as DisplayCondition
              );
            }}
            onSearch={() => handleSearch(mpItemCondition)}
            onFolding={(isOpen) => {
              setOpenSearchBox(isOpen);
              setTimeout(() => {
                if (!mpItemCondition?.startDataInsDtm && !mpItemCondition?.endDataInsDtm) {
                  const dateInput = document.querySelector('.datepickerInput') as HTMLInputElement;
                  if (dateInput) dateInput.value = '';
                }
              }, 100);
            }}
          />
        </SearchBoxRow>
        <SearchBoxRow style={{ display: isOpenSearchBox ? 'block' : 'none' }}>
          <InputBox>
            <SearchRows>
              <SearchCols className="colspan31">
                <label>{String(t('mp.label.개선 목적', '개선 목적'))}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={code?.mpImprTypeLvl1}
                  defaultValue={mpItemCondition?.iprvClsfLv1Cd}
                  onChange={(value) => {
                    setMpItemCondition({
                      ...mpItemCondition,
                      iprvClsfLv1Cd: value as string,
                    });
                  }}
                />
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={code?.mpImprTypeLvl2}
                  defaultValue={mpItemCondition?.iprvClsfLv2Cd}
                  onChange={(value) => {
                    setMpItemCondition({
                      ...mpItemCondition,
                      iprvClsfLv2Cd: value as string,
                    });
                    changeSearchConditionByIprvClsfLv2Cd(value);
                  }}
                />
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={code?.mpImprTypeLvl3}
                  defaultValue={mpItemCondition?.iprvClsfLv3Cd}
                  onChange={(value) => {
                    let ecoYn = '';
                    const cd = (code?.mpImprTypeLvl3 || []).filter((o) => o.cmnCd === value);
                    if (cd.length) {
                      const code = ecoYNcodes.filter((o) => o.cmnCdNm === cd[0]?.optValCtn3 || '');
                      ecoYn = code.length > 0 ? (code[0].cmnCd as string) : '';
                    }
                    setMpItemCondition({
                      ...mpItemCondition,
                      iprvClsfLv3Cd: value as string,
                      ecoYn: ecoYn, // NC/EC여부 기본값 설정
                    });
                  }}
                />
              </SearchCols>
              <SearchCols className="width33">
                <label>{String(t('mp.label.NC/EC', 'NC/EC'))}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={ecoYNcodes}
                  defaultValue={mpItemCondition?.ecoYn}
                  onChange={(value) =>
                    setMpItemCondition({ ...mpItemCondition, ecoYn: value as string })
                  }
                />
              </SearchCols>
              <SearchCols className="width33">
                <label htmlFor="fstrYn" style={{ paddingTop: '0px' }}>
                  {t('mp.label.Fast Track 여부', 'Fast Track 여부')}
                </label>
                <Checkbox
                  id="fstrYn"
                  name="fstrYn"
                  css={st.checkbox}
                  className="custom"
                  checked={mpItemCondition?.fstrYn === 'Y'}
                  onClick={(e) => {
                    setMpItemCondition({
                      ...mpItemCondition,
                      fstrYn: mpItemCondition.fstrYn === 'Y' ? 'N' : 'Y',
                    });
                  }}
                  disableRipple
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('mp.label.4MCC 번호', '4MCC 번호'))}</label>
                <CustomInputWithSearch
                  name="m4ChgNo"
                  placeholder={String(
                    t('mp.msg.4MCC 번호를 입력해 주세요.', '4MCC 번호를 입력해 주세요.')
                  )}
                  value={mpItemCondition?.m4ChgNo}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('mp.label.설비ID', '설비ID'))}</label>
                <CustomInputWithSearch
                  name="eqpId"
                  placeholder={String(
                    t('mp.msg.설비ID를 입력해 주세요.', '설비ID를 입력해 주세요.')
                  )}
                  value={mpItemCondition?.eqpId}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('mp.label.담당자', '담당자'))}</label>
                <CustomInputWithSearch
                  name="reqMgrNm"
                  className="find"
                  placeholder={String(
                    t('mp.msg.담당자를 입력해 주세요.', '담당자를 입력해 주세요.')
                  )}
                  value={displayCondition.reqMgrNm}
                  defaultValue={displayCondition.reqMgrNm}
                  onChange={handleDisplayConditionChange}
                  onSearchClick={() => {
                    setEmpPopupCondition({
                      fieldId: 'reqMgrNm',
                      initParam: displayCondition.reqMgrNm || '',
                    });
                    setOpenElmEmpPopup(true);
                  }}
                  onKeyDown={handleMgrKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('com.label.등록일', '등록일'))}</label>
                <DatePickerWrap className="range">
                  <Datepicker
                    inputClassName="datepickerInput"
                    useRange={true}
                    placeholder="YYYY.MM.DD - YYYY.MM.DD"
                    separator="-"
                    displayFormat={'YYYY.MM.DD'}
                    i18n={'ko'}
                    value={{
                      startDate: mpItemCondition?.startDataInsDtm || '',
                      endDate: mpItemCondition?.endDataInsDtm || '',
                    }}
                    onChange={(value) => {
                      setMpItemCondition({
                        ...mpItemCondition,
                        startDataInsDtm: String(value?.startDate || ''),
                        endDataInsDtm: String(value?.endDate || ''),
                      });
                    }}
                  ></Datepicker>
                </DatePickerWrap>
              </SearchCols>
            </SearchRows>
            {props.type !== 'summary' && (
              <SearchRows className="FourCol">
                <SearchCols>
                  <label>
                    {String(t('mp.label.제작사양서 진행 상태', '제작사양서 진행 상태'))}
                  </label>
                  <ComboBox
                    placeholder={String(t('com.label.전체', '전체'))}
                    options={code?.chgProgStatCd || []}
                    defaultValue={mpItemCondition?.spshRevCtn}
                    onChange={(value) => {
                      setMpItemCondition({ ...mpItemCondition, spshRevCtn: value });
                    }}
                  />
                </SearchCols>
                <SearchCols>
                  <label>{String(t('mp.label.수평전개', '수평전개'))}</label>
                  <ComboBox
                    placeholder={String(t('com.label.전체', '전체'))}
                    options={heYNcodes}
                    defaultValue={mpItemCondition?.heYn}
                    onChange={(value) => {
                      setMpItemCondition({ ...mpItemCondition, heYn: value as string });
                    }}
                  />
                </SearchCols>
                <SearchCols>
                  <label>{t('mp.label.Item 번호', 'Item 번호')}</label>
                  <CustomInputWithSearch
                    name="itemId"
                    placeholder={String(
                      t('mp.msg.Item 번호를 입력해 주세요.', 'Item 번호를 입력해 주세요.')
                    )}
                    value={mpItemCondition?.itemId}
                    onChange={handleConditionChange}
                    onKeyDown={handleKeyDown}
                  />
                </SearchCols>
              </SearchRows>
            )}
          </InputBox>
        </SearchBoxRow>
      </SearchBox>
      {isOpenElmEmpPopup && (
        <ElmEmpPopup
          setUserData={setUserData}
          setPopup={setOpenElmEmpPopup}
          initParam={empPopupCondition.initParam}
          fieldId={empPopupCondition.fieldId}
        />
      )}

      {isOpenEquipmentGroupPopUp && (
        <EquipmentGroupPopUp
          open={isOpenEquipmentGroupPopUp}
          close={() => setOpenEequipmentGroupPopUp(false)}
          type={'mp'}
          condition={{
            equipmentGroup: mpItemCondition.eqclId || '',
          }}
          onCallback={(value) => {
            setMpItemCondition({
              ...mpItemCondition,
              eqclId: (value as EquipmentGroupCondition).equipmentGroup,
            });
            // 설비군이 NND 이면 공정을 ELECTRODE로 고정
            if ((value as EquipmentGroupCondition).equipmentGroup === 'NND') {
              setMpItemCondition({
                ...mpItemCondition,
                procCds: ['E'],
                procCd: 'E',
                eqclId: (value as EquipmentGroupCondition).equipmentGroup,
              });
            }
          }}
        />
      )}

      {isOpenMpMyBookmarkModal && (
        <MpMyBookmarkModal
          open={isOpenMpMyBookmarkModal}
          close={() => setOpenMpMyBookmarkModal(false)}
        />
      )}
    </>
  );
};

export default MPCommonSearch;
